import React from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { Switch, Route, useParams, Redirect } from "react-router-dom";
import { Container, Loader } from "semantic-ui-react";
import { ToastProvider, DefaultToast } from "react-toast-notifications";
import { fetchGetProducts, hasProduct } from "../../queries/products";
import { CommunicationsPublicView } from "../notifications/routes/public_view";
import { Unsubscribe } from "../notifications/routes/unsubscribe";
import { Subscribe } from "../notifications/routes/subscribe";
import { SubscriptionGroups } from "../notifications/routes/subscription_groups";
import { PublicLogin } from "../auth/components/PublicLogin";
import PublicInspections from "../permits/components/inspections/PublicInspections";
import { PublicRegister } from "../auth/components/PublicRegister";
import CitizenAccount from "../permits/components/CitizenAccount";
import Products from "../../constants/products";
import PermitsApply from "../permits/routes/public/PermitsApply";
import CitizenList from "../permits/routes/public/PermitsCitizenList";
import PermitStatement from "../permits/routes/PermitPrinting";
import PermitsFirstPage from "../permits/routes/public/PermitsFirstPage";
import { PermitsPaymentPage } from "../permits/routes/public/PermitsPayment";
import { PermitsPublicLanding } from "../permits/routes/public/PermitsPublicLanding";
import PermitView from "../permits/routes/public/PermitView";
import PermitsDetails from "../permits/routes/public/PermitDetails";
import Page404 from "../../components/global_components/Page404";

import "./PublicApp.scss";
import { useFocusOnPageChange } from "../../hooks/useFocusOnPageChange";
import { PublicMobileAppDownloadPage } from "../notifications/routes/PublicMobileAppDownloadPage";
import PublicForgotPassword from "../auth/components/PublicForgotPassword";
import PublicResetPassword from "../auth/pages/PublicResetPassword";
import { PublicSubscriptionGroupsPage } from "../notifications/routes/notify/public/subscription-groups/PublicSubscriptionGroupsPage";
import { PublicPostsPage } from "../notifications/routes/notify/public/posts/PublicPostsPage";
import SocialMediaPreview from "../notifications/components/SocialMediaPreview";
import { useFeatureFlag } from "../../hooks/use-feature-flag";
import { NotifyContentPublicDetailsPage } from "../notifications/routes/notify/public/posts/NotifyContentDetailsPage";
import PublicVerifyEmail from "../auth/pages/PublicVerifyEmail";
import { useLoginToast } from "../../hooks/useLoginToast";
import DashboardPreview from "../app-preview/pages/DashboardPreview";
import NotificationPreview from "../app-preview/pages/NotificationPreview";
import CreateRequestPreview from "../app-preview/pages/CreateRequestPreview";

const PublicApp = () => {
  const params = useParams<{ site: string }>();
  const dispatch = useDispatch();
  const productsQuery = useQuery(["products", params.site], fetchGetProducts(dispatch));
  const hasCommunications = hasProduct(productsQuery, Products.COMMUNICATIONS);
  const hasPermits = hasProduct(productsQuery, Products.PERMITS);

  // TODO: Remove this when we release the Notify V2 public portal
  const { isEnabled: allowNotifyPublicPortal } = useFeatureFlag("notify.portal");

  useFocusOnPageChange();
  useLoginToast();

  const CustomToast = ({ children, ...props }: any) => (
    <DefaultToast {...props}>
      <div aria-live='polite'>{children}</div>
    </DefaultToast>
  );

  if (productsQuery.isLoading) {
    return (
      <Container className='tw-flex tw-h-full tw-items-center tw-justify-center'>
        <Loader active>Loading...</Loader>
      </Container>
    );
  }

  return (
    <ToastProvider components={{ Toast: CustomToast }}>
      <div className='public-app'>
        <Switch>
          {/* Login */}
          {<Route path='/:site/public/login' component={PublicLogin} />}
          {<Route path='/:site/public/register' component={PublicRegister} />}
          {<Route path='/:site/public/forgot' component={PublicForgotPassword} />}
          {<Route path='/:site/public/reset' component={PublicResetPassword} />}
          {<Route path='/:site/public/verify' component={PublicVerifyEmail} />}

          {/* Notify */}
          {hasCommunications && (
            <Route path='/:site/public/communications/subscriptions' component={SubscriptionGroups} />
          )}
          {hasCommunications && <Route path='/:site/public/communications/unsubscribe' component={Unsubscribe} />}
          {hasCommunications && <Route path='/:site/public/communications/subscribe' component={Subscribe} />}
          {hasCommunications && (
            <Route path='/:site/public/communications/:id/social-media-preview' component={SocialMediaPreview} />
          )}
          {hasCommunications && <Route path='/:site/public/communications/:id' component={CommunicationsPublicView} />}

          {/* Notify V2 */}
          {hasCommunications && allowNotifyPublicPortal && (
            <Route path='/:site/public/notify/subscription-groups' component={PublicSubscriptionGroupsPage} />
          )}
          {hasCommunications && allowNotifyPublicPortal && (
            <Route path='/:site/public/notify/posts/:id' component={NotifyContentPublicDetailsPage} />
          )}
          {hasCommunications && allowNotifyPublicPortal && (
            <Route path='/:site/public/notify/posts' component={PublicPostsPage} />
          )}
          {hasCommunications && allowNotifyPublicPortal && (
            <Redirect exact from='/:site/public/notify' to='/:site/public/notify/subscription-groups' />
          )}

          {/* Permits */}
          {hasPermits && <Route path='/:site/public/permits/apply' component={PermitsApply} />}
          {hasPermits && <Route path='/:site/public/permits/details/:id/print' component={PermitStatement} />}
          {hasPermits && <Route path='/:site/public/permits/details/:id' component={PermitsDetails} />}
          {hasPermits && <Route path='/:site/public/permits/view/:id' component={PermitView} />}
          {hasPermits && <Route path='/:site/public/permits/inspections/:id' component={PublicInspections} />}
          {hasPermits && <Route path='/:site/public/permits/applications/:id' component={PermitsFirstPage} />}
          {hasPermits && <Route path='/:site/public/permits/list' component={CitizenList} />}
          {hasPermits && <Route path='/:site/public/permits/pay/:id' component={PermitsPaymentPage} />}
          {hasPermits && <Route path='/:site/public/permits' component={PermitsPublicLanding} />}
          {hasPermits && <Route path='/:site/public/account' component={CitizenAccount} />}

          {/* Misc */}
          <Route path='/:site/public/mobile-app-download' component={PublicMobileAppDownloadPage} />

          {/* App previews for app store screenshot generation */}
          <Route path='/:site/public/app-preview/dashboard' component={DashboardPreview} />
          <Route path='/:site/public/app-preview/notification' component={NotificationPreview} />
          <Route path='/:site/public/app-preview/create-request' component={CreateRequestPreview} />

          <Route component={Page404} />
        </Switch>
      </div>
    </ToastProvider>
  );
};

export default PublicApp;
